import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { applyPricing, currentPricing, normalizePricing, pricingCents, savingPercent } from '../../config/plans';
import { errorBox, inputClass, noticeBox, primaryButton, secondaryButton } from '../AuthLayout';

const MODE_INFO = {
  test: { name: 'Sandbox', sub: 'Test mode: fake cards, no real money', keyPrefix: 'sk_test_ or rk_test_' },
  live: { name: 'Production', sub: 'Live mode: real customers, real charges', keyPrefix: 'sk_live_ or rk_live_' },
};

async function call(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke('admin-stripe', { body: { action, ...payload } });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data;
}

const money = (cents, cur = 'usd') => (cents == null ? '' : new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.toUpperCase() }).format(cents / 100));
const when = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

function Dot({ ok }) {
  return <span aria-hidden="true" className={`inline-block w-2 h-2 rounded-full shrink-0 ${ok ? 'bg-blue' : 'bg-coral'}`} />;
}

/** Admin console → Payments: Stripe keys per mode (stored encrypted in Vault) and the sandbox/production switch. */
export default function Payments() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try { setStatus(await call('status')); } catch (e) { setError(e.message); }
  }, []);
  const [applyFor, setApplyFor] = useState(0); // bumps after saving prices: each mode offers "Apply to Stripe"
  useEffect(() => { if (status?.pricing) applyPricing(status.pricing); }, [status?.pricing]);
  useEffect(() => { load(); }, [load]);

  if (!status) return error ? <div className={errorBox}>{error}</div> : <p className="text-ink-muted">Loading…</p>;

  return (
    <div className="space-y-6 max-w-5xl">
      {error && <div className={errorBox}>{error}</div>}
      <ModeSwitch status={status} onAsk={setSwitching} />
      {switching && (
        <SwitchConfirm status={status} target={switching} onCancel={() => setSwitching(null)}
          onDone={(s) => { setStatus(s); setSwitching(null); }} />
      )}
      <PricesPanel status={status} onSaved={(s) => { setStatus(s); setApplyFor((n) => n + 1); }} />
      <div className="grid gap-6 lg:grid-cols-2">
        {['test', 'live'].map((m) => (
          <ModeKeys key={m} mode={m} status={status} onStatus={setStatus} applyFor={applyFor} />
        ))}
      </div>
      <p className="text-xs text-ink-muted">
        Keys are stored encrypted in Supabase Vault and are only read by Carduzo's server functions. This page never shows a saved key again, only its first and last characters.
        A restricted key (rk_…) works too, with write access to Customers, Checkout Sessions, Customer portal, Products, Prices, Subscriptions and Webhook endpoints, and read access to Invoices.
      </p>
    </div>
  );
}

function ModeSwitch({ status, onAsk }) {
  const live = status.mode === 'live';
  return (
    <section className={`rounded-2xl border p-5 sm:p-6 ${live ? 'bg-navy text-white border-navy' : 'bg-white border-line'}`}>
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <p className={`text-xs font-semibold tracking-wider uppercase ${live ? 'text-[#C9D6E6]' : 'text-ink-muted'}`}>Payments are running in</p>
          <p className="text-2xl font-extrabold mt-1 flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${live ? 'bg-coral' : 'bg-blue'}`} />
            {MODE_INFO[status.mode].name}
          </p>
          <p className={`text-sm mt-1 ${live ? 'text-[#C9D6E6]' : 'text-ink-muted'}`}>
            {MODE_INFO[status.mode].sub}{status.mode_updated_at ? ` · since ${when(status.mode_updated_at)}` : ''}
          </p>
        </div>
        <div role="radiogroup" aria-label="Payment mode" className={`inline-flex p-1 rounded-xl ${live ? 'bg-white/10' : 'bg-surface border border-line'}`}>
          {['test', 'live'].map((m) => {
            const on = status.mode === m;
            return (
              <button key={m} type="button" role="radio" aria-checked={on} onClick={() => !on && onAsk(m)}
                className={`h-10 px-4 rounded-lg text-sm font-semibold transition ${on
                  ? (m === 'live' ? 'bg-coral text-navy' : 'bg-blue text-white')
                  : (live ? 'text-white hover:bg-white/10' : 'text-ink hover:bg-white')}`}>
                {MODE_INFO[m].name}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const PRICE_FIELDS = [
  { key: 'pro.month', label: 'Pro, monthly', unit: '$ / month' },
  { key: 'pro.yearTotal', label: 'Pro, yearly', unit: '$ / year' },
  { key: 'teams.month', label: 'Teams, monthly', unit: '$ / seat / month' },
  { key: 'teams.yearTotal', label: 'Teams, yearly', unit: '$ / seat / year' },
  { key: 'teams.minSeats', label: 'Teams minimum seats', unit: 'seats', step: 1 },
  { key: 'trialDays', label: 'Free trial', unit: 'days (0 = none)', step: 1 },
];
const getPath = (o, path) => path.split('.').reduce((v, k) => v?.[k], o);
function setPath(o, path, value) {
  const [a, b] = path.split('.');
  return b ? { ...o, [a]: { ...o[a], [b]: value } } : { ...o, [a]: value };
}
const toForm = (p) => Object.fromEntries(PRICE_FIELDS.map((f) => [f.key, String(getPath(p, f.key))]));
function fromForm(form) {
  let p = {};
  for (const f of PRICE_FIELDS) p = setPath(p, f.key, form[f.key].trim() === '' ? NaN : Number(form[f.key]));
  return p;
}

/** Admin → Payments → Prices: the plan prices shown everywhere and used by Set up / Apply to Stripe. */
function PricesPanel({ status, onSaved }) {
  const saved = normalizePricing(status.pricing) || currentPricing(); // older admin-stripe builds don't send pricing
  const [form, setForm] = useState(() => toForm(saved));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const draft = normalizePricing(fromForm(form));
  const changed = draft && JSON.stringify(draft) !== JSON.stringify(saved);

  const yearly = (plan) => {
    if (!draft) return '';
    const { month, yearTotal } = draft[plan];
    const pct = savingPercent(month, yearTotal);
    const perMonth = money(Math.round((yearTotal / 12) * 100));
    if (pct > 0) return `${perMonth}/mo billed yearly · saves ${pct}% (${money(Math.round((month * 12 - yearTotal) * 100))} a year${plan === 'teams' ? ' per seat' : ''})`;
    return `${perMonth}/mo billed yearly · no saving vs monthly${pct < 0 ? ' (yearly costs more!)' : ''}`;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!draft) return setError('Each price must be at least $0.50; seats and trial days are whole numbers (trial 0–90 days).');
    setBusy(true); setError(''); setMessage('');
    try {
      const res = await call('save_pricing', { pricing: draft });
      applyPricing(res.pricing);
      setForm(toForm(res.pricing));
      onSaved(res.status);
      setMessage('Saved. The sales page, signup and upgrade screens show the new prices now. Apply them to Stripe below so checkout charges them.');
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  return (
    <section className="rounded-2xl border border-line bg-white p-5 sm:p-6 space-y-5" aria-labelledby="prices-title">
      <header>
        <h2 id="prices-title" className="text-lg font-bold">Prices</h2>
        <p className="text-sm text-ink-muted">
          In US dollars. Shown on the sales page, signup and upgrade screens{status.pricing_updated_at ? ` · last changed ${when(status.pricing_updated_at)}` : ''}.
          Existing subscribers keep the price they signed up with; new prices apply to new checkouts once applied to Stripe.
        </p>
      </header>
      <form onSubmit={save} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRICE_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-sm font-semibold">{f.label}</span>
              <span className="flex items-center gap-2 mt-1">
                <input type="number" inputMode="decimal" min={f.step ? (f.key === 'trialDays' ? 0 : 1) : 0.5} step={f.step || 0.01}
                  value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className={`${inputClass} !w-28`} aria-label={f.label} />
                <span className="text-xs text-ink-muted">{f.unit}</span>
              </span>
            </label>
          ))}
        </div>
        {draft && (
          <ul className="text-sm text-ink-muted space-y-1" data-testid="price-preview">
            <li><b className="text-ink">Pro:</b> {yearly('pro')}</li>
            <li><b className="text-ink">Teams:</b> {yearly('teams')} · from {money(Math.round(draft.teams.month * draft.teams.minSeats * 100))}/month for {draft.teams.minSeats} seats</li>
          </ul>
        )}
        {error && <div className={errorBox}>{error}</div>}
        {message && <div className={noticeBox}>{message}</div>}
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy || (draft && !changed)} className={`${primaryButton.replace("w-full ", "")} px-5`}>{busy ? 'Saving…' : 'Save prices'}</button>
          {changed && <button type="button" onClick={() => setForm(toForm(saved))} className="text-sm text-ink-muted underline">Undo changes</button>}
        </div>
      </form>
    </section>
  );
}

function SwitchConfirm({ status, target, onCancel, onDone }) {
  const [reset, setReset] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toLive = target === 'live';
  const keys = status.modes[target];
  const ready = keys.secret_key.set && keys.webhook_secret.set && (!toLive || keys.checked?.ready);

  const go = async () => {
    setBusy(true); setError('');
    try {
      const res = await call('set_mode', { mode: target, reset_sandbox_billing: toLive && reset });
      onDone(res.status);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <section role="dialog" aria-label={`Switch to ${MODE_INFO[target].name}`} className="rounded-2xl border border-line-strong bg-white p-5 sm:p-6 space-y-4">
      <h2 className="text-lg font-bold">Switch to {MODE_INFO[target].name}?</h2>
      {toLive ? (
        <div className="space-y-3 text-sm">
          <p>New checkouts will charge real cards, and the billing buttons will open the live Customer Portal. Sandbox webhook events are ignored from now on.</p>
          {!ready && (
            <div className={errorBox}>
              Production isn't ready yet: save the production secret key and webhook secret, then run <b>Set up Stripe</b> and <b>Check setup</b> until everything is ticked.
            </div>
          )}
          {status.sandbox_billing_orgs > 0 && (
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={reset} onChange={(e) => setReset(e.target.checked)} className="mt-1 w-4 h-4 accent-blue" />
              <span>
                Clear sandbox billing from {status.sandbox_billing_orgs} {status.sandbox_billing_orgs === 1 ? 'account' : 'accounts'}. Test purchases go back to Free, so nobody keeps a paid plan from a fake card. Accounts with free access from Carduzo aren't touched.
              </span>
            </label>
          )}
        </div>
      ) : (
        <p className="text-sm">Checkout will only accept Stripe test cards. Real customers won't be able to pay until you switch back to Production.</p>
      )}
      {error && <div className={errorBox}>{error}</div>}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={go} disabled={busy || !ready} className={`${primaryButton.replace("w-full ", "")} px-5`}>
          {busy ? 'Switching…' : `Switch to ${MODE_INFO[target].name}`}
        </button>
        <button type="button" onClick={onCancel} className={`${secondaryButton} w-auto px-5`}>Cancel</button>
      </div>
    </section>
  );
}

function SavedHint({ info }) {
  if (!info.set) return <span className="text-coral-ink">Not set</span>;
  return (
    <span className="font-mono text-[13px]">
      {info.hint}
      <span className="font-sans text-ink-muted"> · {info.source === 'admin' ? 'saved here' : 'from a function secret'}</span>
    </span>
  );
}

function ModeKeys({ mode, status, onStatus, applyFor }) {
  const info = status.modes[mode];
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [replaceHook, setReplaceHook] = useState(false);
  const [copied, setCopied] = useState(false);
  const check = info.checked;
  const active = status.mode === mode;
  const cents = pricingCents(normalizePricing(status.pricing) || undefined);
  // Prices in Stripe that differ from the saved prices (from the last Check setup)
  const outdated = (check?.prices || []).filter((p) => p.found && p.amount !== cents[p.lookup_key]);

  const run = async (label, fn) => {
    setBusy(label); setError(''); setMessage('');
    try { await fn(); } catch (e) { setError(e.message); }
    setBusy('');
  };

  const save = (e) => {
    e.preventDefault();
    if (!secretKey.trim() && !webhookSecret.trim()) return setError('Paste a key to save.');
    run('save', async () => {
      const s = await call('save_keys', { mode, secret_key: secretKey, webhook_secret: webhookSecret });
      setSecretKey(''); setWebhookSecret('');
      onStatus(s);
      setMessage('Saved.');
    });
  };
  const clear = (field) => run('clear', async () => onStatus(await call('save_keys', { mode, clear: [field] })));
  const doCheck = () => run('check', async () => onStatus((await call('check', { mode })).status));
  const doSetup = () => run('setup', async () => {
    const res = await call('setup', { mode, recreate_webhook: replaceHook });
    onStatus(res.status);
    setReplaceHook(false);
    setMessage([res.created.length ? `Created: ${res.created.join(', ')}.` : 'Everything already existed.', res.note].filter(Boolean).join(' '));
  });
  // Apply the saved prices: same as Set up Stripe, then re-check so the price list is current
  const [appliedFor, setAppliedFor] = useState(0);
  const needsApply = info.secret_key.set && (applyFor > appliedFor || outdated.length > 0);
  const doApply = () => {
    if (mode === 'live' && !window.confirm('Change the prices in Stripe production? New customers pay the new prices; current subscribers keep their old price.')) return;
    run('apply', async () => {
      const res = await call('setup', { mode, recreate_webhook: false });
      onStatus(res.status);
      onStatus((await call('check', { mode })).status);
      setAppliedFor(applyFor);
      setMessage(res.created.length ? `Done: ${res.created.join(', ')}.` : 'Stripe already had these prices.');
    });
  };
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(status.webhook_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  const fieldClass = `${inputClass} font-mono text-sm`;

  return (
    <section className="rounded-2xl border border-line bg-white p-5 sm:p-6 space-y-5 min-w-0">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{MODE_INFO[mode].name} keys</h2>
          <p className="text-sm text-ink-muted">{MODE_INFO[mode].sub}</p>
        </div>
        {active && <span className="h-6 px-2.5 rounded-full bg-blue-soft text-blue text-xs font-semibold inline-flex items-center shrink-0">Active</span>}
      </header>

      {needsApply && (
        <div className="rounded-xl border border-coral/40 bg-coral-soft p-4 space-y-2.5" data-testid={`apply-${mode}`}>
          <p className="text-sm">
            <b>{MODE_INFO[mode].name} doesn't have the saved prices yet.</b>{' '}
            {outdated.length ? `Stripe has ${outdated.map((p) => `${p.lookup_key} at ${money(p.amount, p.currency || 'usd')}`).join(', ')}. ` : ''}
            New customers pay the new prices; current subscribers keep their old price.
          </p>
          <button type="button" onClick={doApply} disabled={!!busy} className={`${primaryButton.replace("w-full ", "")} px-5`}>
            {busy === 'apply' ? 'Applying…' : 'Apply to Stripe'}
          </button>
        </div>
      )}

      <form onSubmit={save} className="space-y-4" autoComplete="off">
        <div>
          <label htmlFor={`sk-${mode}`} className="text-sm font-semibold">Secret key</label>
          <p className="text-xs text-ink-muted mb-1.5">Saved: <SavedHint info={info.secret_key} /></p>
          <input id={`sk-${mode}`} type="text" data-1p-ignore data-lpignore="true" data-form-type="other" style={{ WebkitTextSecurity: 'disc' }} value={secretKey} onChange={(e) => setSecretKey(e.target.value)}
            placeholder={info.secret_key.set ? 'Paste a new key to replace it' : `Starts with ${MODE_INFO[mode].keyPrefix}`}
            autoComplete="off" spellCheck={false} className={fieldClass} />
        </div>
        <div>
          <label htmlFor={`wh-${mode}`} className="text-sm font-semibold">Webhook signing secret</label>
          <p className="text-xs text-ink-muted mb-1.5">Saved: <SavedHint info={info.webhook_secret} /></p>
          <input id={`wh-${mode}`} type="text" data-1p-ignore data-lpignore="true" data-form-type="other" style={{ WebkitTextSecurity: 'disc' }} value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder={info.webhook_secret.set ? 'Paste a new secret to replace it' : 'Starts with whsec_ (or let Set up Stripe create it)'}
            autoComplete="off" spellCheck={false} className={fieldClass} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={!!busy} className={`${primaryButton.replace("w-full ", "")} px-5`}>{busy === 'save' ? 'Checking with Stripe…' : 'Save keys'}</button>
          {info.secret_key.source === 'admin' && !active && (
            <button type="button" onClick={() => clear('secret_key')} disabled={!!busy} className="text-sm text-coral-ink underline">Remove secret key</button>
          )}
          {info.webhook_secret.source === 'admin' && (
            <button type="button" onClick={() => clear('webhook_secret')} disabled={!!busy} className="text-sm text-coral-ink underline">Remove webhook secret</button>
          )}
        </div>
      </form>

      <div className="text-sm">
        <p className="font-semibold">Webhook URL</p>
        <div className="flex items-center gap-2 mt-1">
          <code className="flex-1 min-w-0 truncate bg-surface border border-line rounded-lg px-3 py-2 text-[12px]">{status.webhook_url}</code>
          <button type="button" onClick={copyUrl} className="h-9 px-3 rounded-lg border border-line-strong text-sm font-semibold shrink-0">{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </div>

      <div className="border-t border-line pt-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={doCheck} disabled={!!busy || !info.secret_key.set} className={`${secondaryButton} w-auto px-4`}>
            {busy === 'check' ? 'Checking…' : 'Check setup'}
          </button>
          <button type="button" onClick={doSetup} disabled={!!busy || !info.secret_key.set} className={`${secondaryButton} w-auto px-4`}>
            {busy === 'setup' ? 'Setting up…' : 'Set up Stripe'}
          </button>
        </div>
        <p className="text-xs text-ink-muted">
          <b>Set up Stripe</b> creates anything missing in this mode: the Pro and Teams products, the 4 prices from <b>Prices</b> above ({money(cents.tapzo_pro_month)}/mo, {money(cents.tapzo_pro_year)}/yr, per seat for Teams), the two Customer Portal setups and the webhook. When it creates the webhook, it saves the signing secret for you. If a price in Stripe differs from the saved prices, it is replaced by a new one (current subscribers keep their old price). Everything else that exists is left alone.
        </p>
        {check?.webhook?.found && !info.webhook_secret.set && (
          <label className="flex items-start gap-2.5 text-sm">
            <input type="checkbox" checked={replaceHook} onChange={(e) => setReplaceHook(e.target.checked)} className="mt-1 w-4 h-4 accent-blue" />
            <span>Replace webhook endpoint. Stripe only shows a signing secret once, so Carduzo replaces the endpoint and saves the new secret.</span>
          </label>
        )}
        {error && <div className={errorBox}>{error}</div>}
        {message && <div className={noticeBox}>{message}</div>}
        {check && <CheckResult check={check} />}
      </div>
    </section>
  );
}

function CheckResult({ check }) {
  const rows = [
    ...(check.account ? [{ ok: check.charges_enabled !== false, label: `Account: ${check.account}${check.charges_enabled === false ? ' (charges not enabled yet)' : ''}` }] : []),
    ...check.prices.map((p) => ({ ok: p.found, label: `${p.lookup_key}${p.found ? ` · ${money(p.amount, p.currency || 'usd')}` : ' · missing'}` })),
    ...check.portal.map((p) => ({ ok: p.found, label: `Customer Portal (${p.plan})${p.found ? '' : ' · missing'}` })),
    { ok: check.webhook.found && check.webhook.enabled, label: `Webhook endpoint${check.webhook.found ? (check.webhook.enabled ? '' : ' · disabled') : ' · missing'}` },
    ...(check.webhook.missing_events.length ? [{ ok: false, label: `Missing events: ${check.webhook.missing_events.join(', ')}` }] : []),
    { ok: check.webhook.secret_saved, label: `Webhook signing secret${check.webhook.secret_saved ? ' saved' : ' not saved'}` },
  ];
  return (
    <div className="rounded-xl bg-surface border border-line p-4">
      <p className="text-sm font-semibold flex items-center gap-2">
        <Dot ok={check.ready} /> {check.ready ? 'Ready for payments' : 'Not ready yet'}
        <span className="font-normal text-ink-muted text-xs">· checked {when(check.at)}</span>
      </p>
      <ul className="mt-2 space-y-1.5 text-sm">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-2 min-w-0"><Dot ok={r.ok} /><span className="truncate">{r.label}</span></li>
        ))}
      </ul>
    </div>
  );
}
